// ============================================
// COMPONENT - ModalDetallePedido
// ============================================
// Modal para mostrar los detalles de productos de un pedido

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdClose, MdShoppingCart, MdCancel, MdPayment } from 'react-icons/md';
import { useCompletarPedido, useCartStore, useUIStore, type Pedido, type ProductoVendible } from '@almacenes/core';

interface ModalDetallePedidoProps {
  isOpen: boolean;
  onClose: () => void;
  pedido: Pedido | null;
  onCancelar: (pedidoId: number) => Promise<void>;
}

export const ModalDetallePedido = ({ isOpen, onClose, pedido, onCancelar }: ModalDetallePedidoProps) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  const navigate = useNavigate();
  const completarPedidoMutation = useCompletarPedido();
  const addItem = useCartStore((state) => state.addItem);
  const clearCart = useCartStore((state) => state.clearCart);
  const addToast = useUIStore((state) => state.addToast);

  if (!isOpen || !pedido) return null;

  const handleCancelar = async () => {

    setIsCancelling(true);
    try {
      await onCancelar(pedido.pedidoId);
      onClose();
    } catch (error) {
      console.error('Error al cancelar pedido:', error);
      alert('Error al cancelar el pedido. Por favor, intente nuevamente.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handlePagar = async () => {
    if (!pedido) return;

    setIsPaying(true);
    try {
      // 1. Completar el pedido en el backend
      await completarPedidoMutation.mutateAsync(pedido.pedidoId);
      
      // 2. Limpiar el carrito antes de cargar los productos del pedido
      clearCart();
      
      // 3. Cargar productos al carrito (crear objetos ProductoVendible compatibles)
      pedido.detalles.forEach((detalle) => {
        const productoParaCarrito: ProductoVendible = {
          productoAlmacenId: detalle.productoAlmacenId,
          productoNombre: detalle.nombreProducto,
          precioVentaActual: detalle.precioVentaActual,
          // Campos obligatorios con valores por defecto
          productoId: 0,
          codigo: '',
          imagenUrl: null,
          precioCompraActual: 0,
          stock: 0,
          venderPor: 'U' as const,
          esPack: false,
          esRetail: false,
          categoriaAlmacen: null,
          categoriaGlobal: null,
        };
        
        addItem(productoParaCarrito, detalle.cantidad);
      });
      
      addToast('success', `Pedido #${pedido.pedidoId} completado. Productos cargados en el carrito`);
      
      // 4. Cerrar modal y navegar a ventas
      onClose();
      navigate('/ventas');
    } catch (error: any) {
      console.error('Error al completar pedido:', error);
      addToast('error', error.message || 'Error al procesar el pedido');
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdShoppingCart className="text-xl text-[#43BFFF]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#11194F]">Detalle del Pedido</h2>
              <p className="text-sm text-gray-500">Pedido #{pedido.pedidoId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Información del cliente */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Cliente</p>
              <p className="text-sm font-semibold text-[#11194F]">{pedido.nombreCliente}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Teléfono</p>
              <p className="text-sm font-semibold text-[#11194F]">{pedido.telefonoCliente}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Estado</p>
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  pedido.estado === 'COMPLETADO'
                    ? 'bg-green-100 text-green-700'
                    : pedido.estado === 'CANCELADO'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}
              >
                {pedido.estado}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Fecha de Retiro</p>
              <p className="text-sm font-semibold text-[#11194F]">
                {new Date(pedido.fechaHoraPickup).toLocaleString('es-CL', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Tabla de productos */}
        <div className="p-6">
          <h3 className="text-lg font-bold text-[#11194F] mb-4">Productos del Pedido</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F3F4F9]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-bold text-[#11194F]">Producto</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-[#11194F]">Cantidad</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-[#11194F]">Precio Unit.</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-[#11194F]">Total</th>
                </tr>
              </thead>
              <tbody>
                {pedido.detalles.length > 0 ? (
                  pedido.detalles.map((detalle, index) => (
                    <tr
                      key={detalle.detalleId}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900">{detalle.nombreProducto}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                        {detalle.cantidad}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        ${detalle.precioVentaActual.toLocaleString('es-CL')}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-[#11194F] text-right">
                        ${detalle.total.toLocaleString('es-CL')}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No hay productos en este pedido
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-[#11194F]">Total</span>
              <span className="text-2xl font-bold text-[#43BFFF]">
                ${pedido.total.toLocaleString('es-CL')}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          {pedido.estado === 'PENDIENTE' && (
            <>
              <button
                onClick={handleCancelar}
                disabled={isCancelling || isPaying}
                className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdCancel className="text-xl" />
                {isCancelling ? 'Cancelando...' : 'Cancelar Pedido'}
              </button>
              <button
                onClick={handlePagar}
                disabled={isCancelling || isPaying}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdPayment className="text-xl" />
                {isPaying ? 'Procesando...' : 'Pagar Pedido'}
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
